import React, { Component } from "react";
import PropTypes from "prop-types";

import { noop } from "../../utils";
import { saveNodeType } from "../utils";
import { getXValue } from "../../utils/ChartDataUtil";

import ClickableCircle from "../components/ClickableCircle";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";

class EachAverageLine extends Component {
  constructor(props) {
    super(props);

    this.handlePoint1Drag = this.handlePoint1Drag.bind(this);
    this.handlePoint2Drag = this.handlePoint2Drag.bind(this);
    this.handleLineDragStart = this.handleLineDragStart.bind(this);
    this.handleLineMove = this.handleLineMove.bind(this);
    this.handleHover = this.handleHover.bind(this);

    this.isHover = this.isHover.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = {};

    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleLineDragStart() {
    const { x1, x2 } = this.props;
    this.dragStart = { x1, x2 };
  }

  handleLineMove(moreProps) {
    const { index, onDrag } = this.props;
    const { x1, x2 } = this.dragStart;

    const { xScale, xAccessor, fullData } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const px1 = xScale(x1);
    const px2 = xScale(x2);

    const dx = startPos[0] - mouseXY[0];

    const newX1 = getXValue(xScale, xAccessor, [px1 - dx, 0], fullData);
    const newX2 = getXValue(xScale, xAccessor, [px2 - dx, 0], fullData);

    onDrag(index, {
      x1: newX1,
      x2: newX2,
    });
  }

  handlePoint1Drag(moreProps) {
    const { index, onDrag, x2 } = this.props;
    const { xScale, xAccessor, fullData, mouseXY } = moreProps;

    const newX1 = getXValue(xScale, xAccessor, mouseXY, fullData);

    onDrag(index, {
      x1: newX1,
      x2,
    });
  }

  handlePoint2Drag(moreProps) {
    const { index, onDrag, x1 } = this.props;
    const { xScale, xAccessor, fullData, mouseXY } = moreProps;

    const newX2 = getXValue(xScale, xAccessor, mouseXY, fullData);

    onDrag(index, {
      x1,
      x2: newX2,
    });
  }

  helper(props, moreProps) {
    const { x1, x2 } = props;
    const { xScale, chartConfig, fullData } = moreProps;
    const { yScale } = chartConfig;

    // Convert time coordinates to pixel coordinates
    const px1 = xScale(x1);
    const px2 = xScale(x2);

    // Calculate average from data in range
    const start = Math.min(x1, x2);
    const end = Math.max(x1, x2);

    let sumCloses = 0;
    let validTicks = 0;

    for (let i = start; i <= end; i++) {
      if (!fullData[i]) continue;

      const price = fullData[i].close;
      if (price || price === 0) {
        validTicks++;
        sumCloses += price;
      }
    }

    const average = validTicks > 0 ? sumCloses / validTicks : 0;
    const y = yScale(average);

    return {
      px1,
      px2,
      y,
      average,
      validTicks,
    };
  }

  isHover(moreProps) {
    const { mouseXY, chartConfig } = moreProps;
    const [mx, my] = mouseXY;

    const { px1, px2, y } = this.helper(this.props, moreProps);

    // Check if mouse is near the horizontal line
    const tolerance = 10;

    // Check distance to anchor points
    const distToP1 = Math.sqrt(Math.pow(mx - px1, 2) + Math.pow(my - y, 2));
    const distToP2 = Math.sqrt(Math.pow(mx - px2, 2) + Math.pow(my - y, 2));

    if (distToP1 < tolerance || distToP2 < tolerance) {
      return true;
    }

    // Check if mouse is near horizontal line
    const minX = Math.min(px1, px2);
    const maxX = Math.max(px1, px2);

    if (mx >= minX && mx <= maxX && Math.abs(my - y) < tolerance) {
      return true;
    }

    // Check if mouse is near end markers (vertical tails)
    const tailHeight = 20;
    if (
      (Math.abs(mx - px1) < tolerance && Math.abs(my - y) < tailHeight) ||
      (Math.abs(mx - px2) < tolerance && Math.abs(my - y) < tailHeight)
    ) {
      return true;
    }

    return false;
  }

  renderSVG(moreProps) {
    const { appearance, selected } = this.props;
    const { hover } = this.state;

    const stroke = appearance.stroke?.color || "#2196F3";
    const strokeWidth = appearance.stroke?.width || 2;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined ? appearance.stroke.opacity : 1;
    const strokeDasharray =
      appearance.stroke?.dash === "Solid" ? "none" : "5,5";

    const { px1, px2, y, average, validTicks } = this.helper(
      this.props,
      moreProps,
    );

    // Don't render if no valid data
    if (validTicks === 0) {
      return null;
    }

    const tailHeight = 20;

    return (
      <g>
        {/* Main horizontal average line */}
        <line
          x1={px1}
          y1={y}
          x2={px2}
          y2={y}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />

        {/* End marker at point 1 (vertical tail) */}
        <line
          x1={px1}
          y1={y - tailHeight}
          x2={px1}
          y2={y + tailHeight}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />

        {/* End marker at point 2 (vertical tail) */}
        <line
          x1={px2}
          y1={y - tailHeight}
          x2={px2}
          y2={y + tailHeight}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />

        {/* Drag handles */}
        {(selected || hover) && (
          <g>
            <ClickableCircle
              ref={this.saveNodeType("point1")}
              show={true}
              cx={px1}
              cy={y}
              r={6}
              fill={stroke}
              stroke="#FFFFFF"
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint1Drag}
              onDragComplete={this.props.onDragComplete}
            />
            <ClickableCircle
              ref={this.saveNodeType("point2")}
              show={true}
              cx={px2}
              cy={y}
              r={6}
              fill={stroke}
              stroke="#FFFFFF"
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint2Drag}
              onDragComplete={this.props.onDragComplete}
            />
          </g>
        )}
      </g>
    );
  }

  drawOnCanvas(ctx, moreProps) {
    const { appearance, selected } = this.props;
    const { hover } = this.state;

    const stroke = appearance.stroke?.color || "#2196F3";
    const strokeWidth = appearance.stroke?.width || 2;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined ? appearance.stroke.opacity : 1;
    const strokeDasharray = appearance.stroke?.dash === "Solid" ? [] : [5, 5];

    const { px1, px2, y, validTicks } = this.helper(this.props, moreProps);

    // Don't render if no valid data
    if (validTicks === 0) {
      return;
    }

    const tailHeight = 20;

    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeWidth;
    ctx.globalAlpha = strokeOpacity;

    if (strokeDasharray.length > 0) {
      ctx.setLineDash(strokeDasharray);
    } else {
      ctx.setLineDash([]);
    }

    // Draw main horizontal line
    ctx.beginPath();
    ctx.moveTo(px1, y);
    ctx.lineTo(px2, y);
    ctx.stroke();

    // Draw end markers (vertical tails)
    ctx.beginPath();
    ctx.moveTo(px1, y - tailHeight);
    ctx.lineTo(px1, y + tailHeight);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(px2, y - tailHeight);
    ctx.lineTo(px2, y + tailHeight);
    ctx.stroke();

    // Draw drag handles if selected or hovered
    if (selected || hover) {
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);

      // Draw point 1
      ctx.fillStyle = stroke;
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(px1, y, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      // Draw point 2
      ctx.beginPath();
      ctx.arc(px2, y, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    }

    // Reset context
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
  }

  render() {
    const { x1, x2, hoverText, selected } = this.props;
    const { hover } = this.state;

    if (x2 === null || x2 === undefined) {
      return null;
    }

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    return (
      <g>
        <GenericChartComponent
          isHover={this.isHover}
          onHover={this.handleHover}
          onUnHover={this.handleHover}
          onDragStart={this.handleLineDragStart}
          onDrag={this.handleLineMove}
          onDragComplete={this.props.onDragComplete}
          svgDraw={this.renderSVG}
          canvasToDraw={getMouseCanvas}
          canvasDraw={this.drawOnCanvas}
          interactiveCursorClass="react-stockcharts-move-cursor"
          selected={selected}
          enableDragOnHover={true}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
        />

        <HoverTextNearMouse
          show={hoverTextEnabled && hover && !selected}
          {...restHoverTextProps}
          text={hoverTextUnselected}
        />
      </g>
    );
  }
}

EachAverageLine.propTypes = {
  index: PropTypes.number,

  x1: PropTypes.number.isRequired,
  x2: PropTypes.number,

  appearance: PropTypes.object.isRequired,
  hoverText: PropTypes.object.isRequired,

  selected: PropTypes.bool,

  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
};

EachAverageLine.defaultProps = {
  onDrag: noop,
  onDragComplete: noop,
  selected: false,

  hoverText: {
    enable: false,
  },
};

export default EachAverageLine;
